import { memo } from 'react';
import type { NodeKind, TreeNode } from '@/lib/data/elementTrees';

// kind → icon + tailwind colour classes
const KIND_STYLE: Record<NodeKind, { icon: string; text: string; chip: string; label: string }> = {
  page:     { icon: 'web',         text: 'text-slate-700',   chip: 'bg-slate-100 text-slate-700',     label: 'Page' },
  ui:       { icon: 'widgets',     text: 'text-blue-600',    chip: 'bg-blue-50 text-blue-700',        label: 'UI' },
  handler:  { icon: 'bolt',        text: 'text-primary',     chip: 'bg-orange-50 text-primary',       label: 'Handler' },
  state:    { icon: 'data_object', text: 'text-gray-500',    chip: 'bg-gray-100 text-gray-600',       label: 'State' },
  api:      { icon: 'cable',       text: 'text-indigo-600',  chip: 'bg-indigo-50 text-indigo-700',    label: 'API' },
  route:    { icon: 'api',         text: 'text-emerald-600', chip: 'bg-emerald-50 text-emerald-700',  label: 'Route' },
  service:  { icon: 'build',       text: 'text-teal-600',    chip: 'bg-teal-50 text-teal-700',        label: 'Service' },
  external: { icon: 'dns',         text: 'text-purple-600',  chip: 'bg-purple-50 text-purple-700',    label: 'External' },
  note:     { icon: 'info',        text: 'text-amber-600',   chip: 'bg-amber-50 text-amber-700',      label: 'Note' },
};

export const KIND_STYLES = KIND_STYLE;

function highlight(label: string, q: string) {
  if (!q) return label;
  const idx = label.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return label;
  return (
    <>
      {label.slice(0, idx)}
      <mark className="bg-yellow-200 rounded px-0.5">{label.slice(idx, idx + q.length)}</mark>
      {label.slice(idx + q.length)}
    </>
  );
}

interface NodeProps {
  node: TreeNode;
  path: string;
  depth: number;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  query: string;
}

const ElementTreeNode = memo(function ElementTreeNode({ node, path, depth, expanded, onToggle, query }: NodeProps) {
  const hasChildren = !!node.children?.length;
  const isOpen = expanded.has(path);
  const style = node.warn ? KIND_STYLE.note : KIND_STYLE[node.kind];
  const warn = node.warn;

  return (
    <div className="relative">
      <div
        className={`group flex items-start gap-2 rounded-md py-1.5 pr-2 transition-colors ${
          warn ? 'bg-red-50/70 hover:bg-red-50' : 'hover:bg-gray-50'
        }`}
        style={{ paddingLeft: `${depth * 18 + 4}px` }}
      >
        {/* expand toggle */}
        {hasChildren ? (
          <button
            onClick={() => onToggle(path)}
            className="mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-gray-200 text-gray-400"
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            <span className={`material-symbols-outlined text-base transition-transform ${isOpen ? 'rotate-90' : ''}`}>
              chevron_right
            </span>
          </button>
        ) : (
          <span className="mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
          </span>
        )}

        {/* kind icon */}
        <span className={`material-symbols-outlined text-[18px] mt-0.5 flex-shrink-0 ${warn ? 'text-red-500' : style.text}`}>
          {warn ? 'warning' : style.icon}
        </span>

        {/* label + ref */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`text-sm leading-snug ${warn ? 'text-red-700 font-medium' : 'text-gray-800'}`}>
              {highlight(node.label, query)}
            </span>
            {node.ref && (
              <code className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 flex-shrink-0">
                {node.ref}
              </code>
            )}
            {hasChildren && (
              <span className="text-[11px] text-gray-400 flex-shrink-0">{node.children!.length}</span>
            )}
          </div>
        </div>

        {/* kind chip */}
        <span className={`hidden sm:inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${warn ? 'bg-red-100 text-red-700' : style.chip}`}>
          {warn ? 'Issue' : style.label}
        </span>
      </div>

      {/* children */}
      {hasChildren && isOpen && (
        <div className="relative">
          {/* guide line */}
          <div
            className="absolute top-0 bottom-2 w-px bg-gray-200"
            style={{ left: `${depth * 18 + 12}px` }}
          />
          {node.children!.map((child, i) => (
            <ElementTreeNode
              key={`${path}.${i}`}
              node={child}
              path={`${path}.${i}`}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              query={query}
            />
          ))}
        </div>
      )}
    </div>
  );
});

export function ElementTree({
  root, rootPath, expanded, onToggle, query,
}: {
  root: TreeNode;
  rootPath: string;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  query: string;
}) {
  return (
    <div className="text-left">
      <ElementTreeNode node={root} path={rootPath} depth={0} expanded={expanded} onToggle={onToggle} query={query} />
    </div>
  );
}

// Walk every path under a node (for expand-all / search).
export function collectPaths(node: TreeNode, path: string, acc: string[] = []): string[] {
  acc.push(path);
  node.children?.forEach((c, i) => collectPaths(c, `${path}.${i}`, acc));
  return acc;
}

// Paths that should be open so every node matching `q` is visible.
export function pathsMatching(node: TreeNode, path: string, q: string, openAcc: Set<string>): boolean {
  const self = node.label.toLowerCase().includes(q) || (node.ref?.toLowerCase().includes(q) ?? false);
  let childMatch = false;
  node.children?.forEach((c, i) => {
    if (pathsMatching(c, `${path}.${i}`, q, openAcc)) childMatch = true;
  });
  if (childMatch) openAcc.add(path);
  return self || childMatch;
}
